/**
 * Best cards, server half — extraction, caching, and fetch orchestration for the
 * `/best/<scope>` hub enrichments (top-pick disclosure + role-chip peek panels + the
 * cross-guide "Most picked" strip). Types and render helpers live in
 * ./bestCardsShared, which is the only part client components may import.
 *
 * Best-guide picks are STRUCTURED CMS data: every guide body carries
 * `nc-deals-detailed` / in-depth (`isInDepthDeal`) blocks with the ClearBuy product id, pros
 * tags, lab score, price, and a rendered DOM anchor. This module re-reads each
 * article's page payload through the app's data source and extracts the picks. NOTE
 * (pressure-test finding, 2026-08-26): that payload is NOT a cheap CMS read — the
 * best-list mapper hydrates deal blocks from DealHunt per product
 * (`ds/mappers/dealhunt.ts`), so extracted prices are live-at-extraction-time when
 * DealHunt answered and the article's baked strings when it didn't. The rules that
 * shape this module:
 *
 *  - **Same source as the article.** A card is derived from the exact payload the
 *    article page renders from, so it can never disagree with the article AS OF the
 *    extraction moment. Price drift within the cache window is bounded by the same
 *    TTL the article page's own CDN caching implies.
 *  - **Freshness rides the list feed.** `tax_posts` reports each post's UPDATED date
 *    (verified live: an editor update moves it), so the per-article cache is keyed
 *    (slug, updatedAt) — an edited guide misses the cache on the next hub render and
 *    re-extracts, while the other rows stay cached.
 *  - **The request path never waits.** One cold article costs 11–19 s upstream
 *    (measured — the full article codec + DealHunt hydration), so any deadline short
 *    enough to protect TTFB can never be met and any deadline long enough to be met
 *    destroys TTFB. Requests serve whatever the cache holds and kick the fill off in
 *    the background; the page pairs an un-enriched render with a short CDN TTL so
 *    the hub self-heals within ~a minute of going cold.
 *  - **A failed read is never "no picks".** Only a well-formed article payload with
 *    no deal blocks caches as 'none' (6 h); an unparseable/failed read goes to the
 *    60 s failure cache — the SEO-N1 lesson (page.tsx) applied at this layer.
 *  - **Anchors are real.** Role sections render `id-{slug}-{postId}` DOM targets and
 *    every deal module renders `deal-N` (verified in production HTML), so every pick
 *    link lands on the exact spot the product appears.
 *
 * Corpus facts this leans on (36 first-page articles, 3 scopes, 2026-08-26): a role
 * section contains exactly one deal block for its product; roster sections ("Quick
 * picks") hold many; 3–10 picks per guide; plural role sections and product-named
 * sections degrade to roster mode (no chips) by design. Follow-up finding
 * (2026-08-27, guides 7142/156487/2595): even where section markers stop, the body
 * copy carries a colon-anchored role heading per pick — mined into
 * `BestPick.headingRole`, the strip receipts' fallback before "Pick #N".
 */

import { SimpleCache } from 'shared-code/simple-cache'
import { isInDepthDeal } from './inDepthDeal'
import { urlPath } from './versusCardsShared'
import {
  bestCardLookup,
  formatPrice,
  picksSummary,
  type BestCard,
  type BestPick,
  type MostPickedEntry,
  type MostPickedProduct,
} from './bestCardsShared'
import type { EditorialStory } from './types'
import type {
  DSLabeledLink,
  DSNCDealMSRP,
  DSDealButton,
  DSPageSection,
  DSPageSections,
} from 'shared-definitions/types'

// Re-exported so server-side callers (page.tsx, specs) keep one import path.
export { bestCardLookup, formatPrice, picksSummary }
export type { BestCard, BestPick, MostPickedEntry, MostPickedProduct }

/* ── extraction (pure — unit-tested) ───────────────────────────────────────── */

/** Pros shipped per pick; the peek panel renders them all. */
const PROS_CAP = 4

/**
 * Section labels that are page furniture, never pick roles. A one-deal section with
 * one of these labels keeps the anchor but contributes no role by itself; a later
 * real role section may still claim the pick (see buildCard).
 * `\bethic` not `ethic`: "Best aesthetics" is a role, "Ethics" is furniture.
 */
const BOILERPLATE_LABEL =
  /overview|quick answer|quick awnser|short answer|long answer|in-depth|detailed answer|notable|buying guide|how we|why trust|who am i|\bethic|faq|quick pick/i

/**
 * A role label must SAY it's a role. Corpus labels universally carry one of these
 * ("Our pick", "Best overall", "ANC pick", "Budget iPhone pick", "Best alternative
 * to the JBL Flip 7"); labels without any — "Sonos Roam", "AirPods Max" — are
 * product names used as headings, and rendering them as role chips both reads wrong
 * and can attribute one product's name to another (observed live: a "Sonos Roam"
 * section holding a Sony ULT Field 1 deal block). Omission-first, per the standing
 * constraint: never fabricate a role.
 */
const ROLE_WORD =
  /\b(pick|picks|best|top|budget|premium|value|overall|runner|choice|upgrade|alternative|alternatives|under|over|cheap|favorite|favourite|winner)\b/i

interface DealNode {
  kind: 'deal'
  productId: number | null
  title: string
  score: number | null
  tags: string[]
  anchor: string | null
  price: number | null
  currency: string | null
  retailer: string | null
  reviewUrl: string | null
}

interface SectionNode {
  kind: 'section'
  anchor: string
}

/** An inline `<h2>`/`<h3>` from the article body (nc-string html) — the editors
 *  write a role heading above nearly every pick ("Best wireless headphones for
 *  travel: Bose QuietComfort Ultra…", roster-style "The best overall:") even when
 *  they stop adding section markers (verified: guides 7142, 156487, 2595 all carry
 *  one per pick while their section markers cover at most a third of them). */
interface HeadingNode {
  kind: 'heading'
  text: string
}

type StreamNode = DealNode | SectionNode | HeadingNode

/** The structural slice of a page this module reads — resource-guarded, not cast. */
interface PageLike {
  blocks?: unknown
  sections?: DSPageSections | null
}

interface RawDealBlock {
  resource: string
  clearBuyProductId?: number | null
  title?: unknown
  score?: number | null
  tags?: readonly unknown[]
  /** Absent on `nc-deals-large-alt`, which renders no anchor. */
  anchor?: string | null
  buttons?: readonly DSDealButton[]
  msrp?: DSNCDealMSRP | null
  /** "See review" link — roster (nc-deals-detailed) blocks only; in-depth copies ship null or none. */
  refLink?: DSLabeledLink | null
}

function dealNode(block: RawDealBlock): DealNode | null {
  if (typeof block.title !== 'string' || block.title.length === 0) {
    return null
  }
  const button = block.buttons?.[0] ?? null
  // WP sends current: 0 for "See price at …" buttons — an unknown price, not a free
  // product. Verified live: without the guard, spans read "5 picks · $0–$159".
  const current = positive(button?.price?.current)
  const msrp = block.msrp ?? null
  const retailer = /\bat (.+)$/.exec(button?.link?.label ?? '')?.[1] ?? null
  return {
    kind: 'deal',
    productId: typeof block.clearBuyProductId === 'number' ? block.clearBuyProductId : null,
    title: block.title,
    score: typeof block.score === 'number' && block.score > 0 ? block.score : null,
    tags: (block.tags ?? [])
      .filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
      .map(t => t.trim())
      .slice(0, PROS_CAP),
    anchor: typeof block.anchor === 'string' && block.anchor ? block.anchor : null,
    price: current ?? positive(msrp?.price),
    currency: button?.price?.currency ?? msrp?.currency ?? null,
    retailer,
    reviewUrl: sgReviewPath(block.refLink?.pLink?.href),
  }
}

/**
 * App-relative path of a SoundGuys review href, or null. Guides occasionally cite a
 * sibling site's review through the same refLink ("See review" on the Huawei
 * FreeClip 2 points at androidauthority.com — verified live 2026-08-28); a review
 * link on a SoundGuys hub that silently leaves the site is a trap, so only
 * soundguys.com hrefs survive, converted to this app's own route.
 */
function sgReviewPath(href: unknown): string | null {
  if (typeof href !== 'string' || href.length === 0) {
    return null
  }
  if (href.startsWith('/')) {
    return href.replace(/\/+$/, '') || null
  }
  try {
    const url = new URL(href)
    if (!/(^|\.)soundguys\.com$/i.test(url.hostname)) {
      return null
    }
    return url.pathname.replace(/\/+$/, '') || null
  } catch {
    return null
  }
}

function positive(value: number | null | undefined): number | null {
  return typeof value === 'number' && value > 0 ? value : null
}

/**
 * Deals and section markers in document order. The body block tree nests (multi
 * blocks, ad wrappers), so this walks structurally rather than trusting one level —
 * the same traversal the corpus sweep validated against all 36 live articles.
 */
function blockStream(blocks: unknown): StreamNode[] {
  const out: StreamNode[] = []
  const walk = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(walk)
      return
    }
    if (typeof value !== 'object' || value === null) {
      return
    }
    const node = value as { resource?: unknown; anchor?: unknown; html?: unknown }
    if (node.resource === 'nc-section' && typeof node.anchor === 'string') {
      out.push({ kind: 'section', anchor: node.anchor })
    } else if (node.resource === 'nc-deals-detailed' || isInDepthDeal(node)) {
      const deal = dealNode(value as RawDealBlock)
      if (deal) {
        out.push(deal)
      }
    } else if (node.resource === 'nc-string' && typeof node.html === 'string') {
      for (const text of headingTexts(node.html)) {
        out.push({ kind: 'heading', text })
      }
    }
    Object.values(value).forEach(walk)
  }
  walk(blocks)
  return out
}

/** `<h2>`/`<h3>` inner texts of one nc-string, tags stripped, entities decoded. */
function headingTexts(html: string): string[] {
  const out: string[] = []
  for (const match of html.matchAll(/<h([23])[^>]*>(.*?)<\/h\1>/gis)) {
    const text = match[2]
      .replace(/<[^>]+>/g, ' ')
      .replace(/&#(\d+);/g, (_, n: string) => String.fromCharCode(Number(n)))
      .replace(/&amp;/gi, '&')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    if (text) {
      out.push(text)
    }
  }
  return out
}

/**
 * The role an inline heading gives THIS deal, or null. Two editor idioms, both
 * colon-anchored (a colon is required — plain headings stay furniture):
 *  - "role: product" ("Best value headphones for working out: JLab JBuds Lux ANC")
 *    — accepted only when the product side names this very deal (normalized
 *    inclusion; token overlap alone would let "…: Sony WH-1000XM6" claim an XM5).
 *  - "role:" with nothing after (roster style, "The best overall:") — the heading
 *    hangs directly above its deal block, so adjacency is the whole claim.
 * The role side must carry a role word and no boilerplate. The bare-colon form
 * additionally passes the full section-label gates (labelIsRole) since adjacency is
 * its only tie to the deal; the "role: product" form skips the token-overlap part —
 * a matched product side is a stronger claim than the overlap heuristic ("Best
 * open-ear …: Nothing Ear (Open)" legitimately shares two tokens with its product)
 * — but still refuses a role that just restates the product name. Omission-first,
 * never fabricate.
 */
function headingRoleFor(text: string, deal: DealNode): string | null {
  const match = /^(.+?):\s*(.*)$/.exec(text)
  if (!match) {
    return null
  }
  const rolePart = match[1].trim()
  const productPart = match[2].trim()
  if (BOILERPLATE_LABEL.test(rolePart)) {
    return null
  }
  if (productPart) {
    const productNorm = normalize(productPart)
    const titleNorm = normalize(deal.title)
    const roleNorm = normalize(rolePart)
    if (!productNorm.includes(titleNorm) && !titleNorm.includes(productNorm)) {
      return null
    }
    if (!ROLE_WORD.test(rolePart) || roleNorm.includes(titleNorm)) {
      return null
    }
  } else if (!labelIsRole(rolePart, deal.title)) {
    return null
  }
  // Same trim the strip applies to guide titles: "The best overall" → "Best overall".
  return rolePart.replace(/^The best\b/i, 'Best')
}

function sectionLabels(sections: DSPageSections | null | undefined): Map<string, string> {
  const labels = new Map<string, string>()
  const add = (items: readonly DSPageSection[]): void =>
    items.forEach(item => {
      labels.set(item.anchor, item.label.trim())
      add(item.children)
    })
  add(sections?.items ?? [])
  return labels
}

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * Is this section label a usable ROLE for this deal? Three gates, omission-first:
 * a role word must be present ("Sonos Roam" fails), the label must not restate the
 * product name ("JBL Clip 5" fails by substring — short audio names like "KEF LSX
 * II" carry no two long tokens, so a token count alone structurally cannot catch
 * them), and a label sharing 2+ whole tokens with the product name ("Best
 * alternative to the JBL Flip 7: Soundcore Motion 300") is the product's heading,
 * not a role.
 */
function labelIsRole(label: string, dealTitle: string): boolean {
  if (!ROLE_WORD.test(label)) {
    return false
  }
  const labelNorm = normalize(label)
  const titleNorm = normalize(dealTitle)
  if (labelNorm.includes(titleNorm) || titleNorm.includes(labelNorm)) {
    return false
  }
  const labelTokens = new Set(labelNorm.split(' '))
  const shared = new Set(titleNorm.split(' ').filter(word => labelTokens.has(word)))
  return shared.size < 2
}

/**
 * One guide's card from its own page payload. Null when the page carries no deal
 * blocks at all (news roundups in the category) — the row then renders bare.
 */
export function extractBestCard(page: PageLike, url: string): BestCard | null {
  return buildCard(blockStream(page.blocks), sectionLabels(page.sections), url)
}

function buildCard(
  stream: StreamNode[],
  labels: Map<string, string>,
  url: string
): BestCard | null {
  // Segment the stream: which deals sit inside which section.
  const segments: { anchor: string | null; deals: DealNode[] }[] = [{ anchor: null, deals: [] }]
  for (const node of stream) {
    if (node.kind === 'section') {
      segments.push({ anchor: node.anchor, deals: [] })
    } else if (node.kind === 'deal') {
      segments[segments.length - 1].deals.push(node)
    }
  }

  // A guide sometimes links only one of a product's two blocks (roster copy has the
  // ClearBuy id, role-section copy doesn't, or vice versa). Resolve titles to ids
  // first so the two copies share one key instead of splitting into two picks.
  const idByTitle = new Map<string, number>()
  for (const segment of segments) {
    for (const deal of segment.deals) {
      if (deal.productId !== null) {
        idByTitle.set(normalize(deal.title), deal.productId)
      }
    }
  }
  const productIdOf = (deal: DealNode): number | null =>
    deal.productId ?? idByTitle.get(normalize(deal.title)) ?? null
  const keyOf = (deal: DealNode): string => {
    const id = productIdOf(deal)
    return id !== null ? `id:${id}` : `title:${normalize(deal.title)}`
  }

  // The "See review" refLink ships only on the roster's detailed copy; the role
  // section's large copy carries null — resolve it across a product's copies the
  // same way ids are, so role-section-first guides still get the link.
  const reviewUrlByKey = new Map<string, string>()
  for (const segment of segments) {
    for (const deal of segment.deals) {
      if (deal.reviewUrl !== null && !reviewUrlByKey.has(keyOf(deal))) {
        reviewUrlByKey.set(keyOf(deal), deal.reviewUrl)
      }
    }
  }

  // A section holding exactly ONE deal is that product's own section — its label is
  // the pick's role (unless boilerplate or a product name) and its anchor the
  // landing spot. A boilerplate section ("Quick answer" often opens with the top
  // pick's deal block) records the anchor only, and a LATER real role section may
  // still upgrade the entry — first-real-role wins, not first-section.
  const roleByKey = new Map<string, { role: string | null; anchor: string }>()
  for (const segment of segments) {
    if (segment.anchor === null || segment.deals.length !== 1) {
      continue
    }
    const [deal] = segment.deals
    const key = keyOf(deal)
    const existing = roleByKey.get(key)
    if (existing && existing.role !== null) {
      continue
    }
    const label = labels.get(segment.anchor) ?? null
    const role =
      label && !BOILERPLATE_LABEL.test(label) && labelIsRole(label, deal.title) ? label : null
    if (!existing) {
      roleByKey.set(key, { role, anchor: segment.anchor })
    } else if (role !== null) {
      roleByKey.set(key, { role, anchor: segment.anchor })
    }
  }

  // Inline heading roles: a heading claims exactly the NEXT deal — cleared by any
  // deal (claimed or not) or section marker, so a stale "Best X:" can never drift
  // onto a later unrelated block. First non-null claim per product wins: the roster
  // instance comes first in document order and carries the editors' short form
  // ("The best under $100:") over the in-depth restatement.
  const headingByKey = new Map<string, string>()
  let pendingHeading: string | null = null
  for (const node of stream) {
    if (node.kind === 'heading') {
      pendingHeading = node.text
    } else if (node.kind === 'section') {
      pendingHeading = null
    } else {
      if (pendingHeading !== null) {
        const key = keyOf(node)
        const headingRole = headingRoleFor(pendingHeading, node)
        if (headingRole !== null && !headingByKey.has(key)) {
          headingByKey.set(key, headingRole)
        }
      }
      pendingHeading = null
    }
  }

  // Rank order = first appearance in the stream, one entry per product.
  const picks: BestPick[] = []
  const seen = new Set<string>()
  for (const segment of segments) {
    for (const deal of segment.deals) {
      const key = keyOf(deal)
      if (seen.has(key)) {
        continue
      }
      seen.add(key)
      const roleSection = roleByKey.get(key) ?? null
      picks.push({
        name: deal.title,
        productId: productIdOf(deal),
        score: deal.score,
        pros: deal.tags,
        price: deal.price,
        currency: deal.currency,
        retailer: deal.retailer,
        role: roleSection?.role ?? null,
        headingRole: headingByKey.get(key) ?? null,
        anchor: roleSection?.anchor ?? deal.anchor,
        reviewUrl: reviewUrlByKey.get(key) ?? null,
      })
    }
  }
  if (picks.length === 0) {
    return null
  }

  const prices = picks.map(p => p.price).filter((p): p is number => p !== null)
  return {
    url,
    mode: picks.length >= 2 && picks.every(p => p.role !== null) ? 'roles' : 'roster',
    picks,
    priceMin: prices.length ? Math.min(...prices) : null,
    priceMax: prices.length ? Math.max(...prices) : null,
  }
}

/* ── cross-guide aggregation ───────────────────────────────────────────────── */

/** How many strip cards the hub shows. */
const MOST_PICKED_CAP = 6

/**
 * The products this page's guides keep converging on — every product picked by two
 * or more of the rendered guides, strongest first, each entry carrying its receipts
 * (role + guide + anchor). Computed per page render from the already-extracted cards;
 * empty when no product repeats.
 */
export function computeMostPicked(
  cards: BestCard[],
  stories: readonly { url: string; title: string }[]
): MostPickedProduct[] {
  const titleByUrl = new Map(stories.map(s => [s.url, s.title]))
  const products = new Map<number, MostPickedProduct>()
  for (const card of cards) {
    const articleTitle = titleByUrl.get(card.url)
    if (!articleTitle) {
      continue
    }
    card.picks.forEach((pick, index) => {
      if (pick.productId === null) {
        return
      }
      const existing = products.get(pick.productId) ?? {
        name: pick.name,
        productId: pick.productId,
        score: null,
        price: null,
        currency: null,
        reviewUrl: null,
        imageUrl: null,
        entries: [],
      }
      if (pick.score !== null && (existing.score === null || pick.score > existing.score)) {
        existing.score = pick.score
      }
      if (existing.price === null && pick.price !== null) {
        existing.price = pick.price
        existing.currency = pick.currency
      }
      if (existing.reviewUrl === null && pick.reviewUrl !== null) {
        existing.reviewUrl = pick.reviewUrl
      }
      existing.entries.push({
        role: pick.role,
        headingRole: pick.headingRole,
        rank: index + 1,
        articleTitle,
        articleUrl: card.url,
        anchor: pick.anchor,
      })
      products.set(pick.productId, existing)
    })
  }
  return [...products.values()]
    .filter(product => product.entries.length > 1)
    .sort((a, b) => b.entries.length - a.entries.length || (b.score ?? 0) - (a.score ?? 0))
    .slice(0, MOST_PICKED_CAP)
}

/* ── fetch orchestration ───────────────────────────────────────────────────── */

/**
 * Extracted cards are tiny and the cache key already carries the article's updated
 * date, so the TTL is a janitor, not the freshness mechanism: an edit rotates the key
 * immediately; unedited articles re-extract at most every REFRESH window.
 */
const CARD_TTL_MS = 6 * 60 * 60 * 1000
/** `'none'` marks WELL-FORMED pages with no picks (news roundups in the category) —
 *  cached the full TTL so they don't refetch. Failed/unparseable reads never land
 *  here; they take the 60 s failure cache below. */
const cardCache = new SimpleCache<BestCard | 'none'>(CARD_TTL_MS)

/** After a failed or unparseable article read, don't retry that article for this long. */
const FAILURE_TTL_MS = 60 * 1000
const failedCache = new SimpleCache<true>(FAILURE_TTL_MS)

/** One in-flight extraction per article, shared across concurrent renders. */
const inflight = new Map<string, Promise<void>>()

/** WordPress degrades under fan-out (see lib/editorial/wp.ts) — a cold page render
 *  is up to 12 article fetches, paced a few at a time. */
const CONCURRENCY = 4

function cacheKey(path: string, story: EditorialStory): string {
  // `updatedAt` may be undefined on stories deserialized from pre-field cached
  // documents — treated as a stable empty segment, so those still cache by path.
  return `${path}:${story.updatedAt ?? ''}`
}

export interface ResolvedBestCard {
  story: EditorialStory
  /** The guide's URL path without its leading slash. */
  slug: string
  /** Extracted card, `'none'` (well-formed article, genuinely no picks), or `null`
   *  (not resolved yet — cold cache or recent failure; a background fill was kicked). */
  card: BestCard | 'none' | null
}

/**
 * Per-story card resolution, joined by story URL — CACHE-READ-ONLY. Missing articles
 * are queued for a background fill and this call answers without them: one cold
 * article costs 11–19 s upstream (full article codec + DealHunt hydration —
 * measured), so no affordable deadline can ever be met on the request path.
 * Callers that must not serve a partial corpus (the best-rosters feed) check for
 * `card: null` entries and refuse instead.
 */
export function resolveBestCards(stories: readonly EditorialStory[]): ResolvedBestCard[] {
  const tasks = stories.flatMap(story => {
    const path = urlPath(story.url)
    if (!path) {
      return []
    }
    return [{ story, slug: path.replace(/^\//, ''), key: cacheKey(path, story) }]
  })

  const resolved: ResolvedBestCard[] = []
  const pending: typeof tasks = []
  for (const task of tasks) {
    const cached = cardCache.get(task.key)
    if (!cached && !failedCache.get(task.key)) {
      pending.push(task)
    }
    resolved.push({ story: task.story, slug: task.slug, card: cached ?? null })
  }

  if (pending.length > 0) {
    // Fire-and-forget: fills cardCache/failedCache for FUTURE renders. Never awaited
    // on the request path; errors are absorbed per task inside.
    void runPool(pending)
  }

  return resolved
}

/**
 * Cards for the hub rows — `resolveBestCards` minus the misses. The page pairs a
 * null result with a short CDN TTL, and the hub self-heals on the first render
 * after the background pool drains (~30–60 s cold). Null when nothing is cached
 * yet — the hub then renders exactly as today.
 */
export function fetchBestCards(stories: readonly EditorialStory[]): BestCard[] | null {
  const resolved = stories.length > 0 ? resolveBestCards(stories) : []
  const cards = resolved.flatMap(r => (r.card && r.card !== 'none' ? [r.card] : []))
  return cards.length > 0 ? cards : null
}

async function runPool(
  tasks: { story: EditorialStory; slug: string; key: string }[]
): Promise<void> {
  const queue = [...tasks]
  const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
    for (let task = queue.shift(); task; task = queue.shift()) {
      await extractOne(task.slug, task.key, task.story.url)
    }
  })
  await Promise.all(workers)
}

function extractOne(slug: string, key: string, storyUrl: string): Promise<void> {
  const shared =
    inflight.get(key) ?? extractArticleCard(slug, key, storyUrl).finally(() => inflight.delete(key))
  inflight.set(key, shared)
  return shared
}

/**
 * Fetch one article's payload, extract, and cache the outcome. The three outcomes
 * are deliberately distinct:
 *  - card        → cached CARD_TTL_MS
 *  - 'none'      → well-formed article, genuinely no deal blocks — cached CARD_TTL_MS
 *  - failure     → thrown, or a payload with no `blocks` (redirects, WP error pages,
 *                  truncated bodies) — 60 s failure cache, retried. A transient WP
 *                  hiccup must never masquerade as "this guide has no picks" for
 *                  6 hours (the SEO-N1 class of bug).
 * Exported for the orchestration spec.
 */
export async function extractArticleCard(
  slug: string,
  key: string,
  storyUrl: string
): Promise<void> {
  try {
    // Lazy: keeps the data-source facade out of any non-server import graph.
    const { getds } = await import('ds')
    const ds = await getds()
    const { data } = await ds.getTplPage(slug)
    const page = data && typeof data === 'object' && 'blocks' in data ? (data as PageLike) : null
    if (!page) {
      failedCache.set(key, true)
      return
    }
    cardCache.set(key, extractBestCard(page, storyUrl) ?? 'none')
  } catch {
    failedCache.set(key, true)
  }
}
